import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Calendar, ArrowRight, Mail, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const registrationId = searchParams.get('registration_id');
  
  const [registration, setRegistration] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (registrationId) {
      fetchRegistration();
    }
  }, [registrationId]);

  const fetchRegistration = async () => {
    try {
      const response = await axios.get(`${API}/registrations/${registrationId}`);
      setRegistration(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const isBookingApproved = registration?.booking_status === 'approved';
  const isBookingPending = registration?.booking_status === 'pending_approval';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="animate-pulse text-slate-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="glass-header sticky top-0 z-30 py-4 px-6 md:px-12">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
            <img 
              src="https://customer-assets.emergentagent.com/job_test-booking-center/artifacts/aqs71l0n_LL.jpg" 
              alt="Logo"
              className="w-10 h-10 rounded-lg"
            />
            <div className="logo-text text-xl">
              English<span className="logo-accent">Test</span>Solution
            </div>
          </div>
        </div>
      </header>

      <main className="py-12 px-6 md:px-12">
        <div className="max-w-2xl mx-auto">
          {/* Success Header */}
          <div className="text-center mb-8 animate-fade-in-up">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-green-500" />
            </div>
            <h1 className="text-3xl md:text-4xl mb-4">Registration Complete!</h1>
            <p className="text-lg text-slate-600">
              Thank you for registering with English Test Solution
            </p>
          </div>

          {/* Status Card */}
          <Card className="shadow-lg mb-6 animate-fade-in-up animation-delay-100">
            <CardContent className="p-6">
              <div className="space-y-6">
                {/* Registration Details */}
                <div>
                  <h3 className="font-semibold text-slate-800 mb-3">Registration Details</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-slate-500">Name</p>
                      <p className="font-medium">{registration?.first_name} {registration?.last_name}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Test</p>
                      <p className="font-medium">{registration?.test_name}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Email</p>
                      <p className="font-medium">{registration?.email}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Total Amount</p>
                      <p className="font-medium">C${registration?.total_amount?.toFixed(2)}</p>
                    </div>
                  </div>
                </div>

                {/* Booking Info */}
                {registration?.booking_date && (
                  <div className="border-t pt-4">
                    <h3 className="font-semibold text-slate-800 mb-3">Test Appointment</h3>
                    <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                      <div className="flex items-center gap-3">
                        <Calendar className="w-6 h-6 text-[#1E3A8A]" />
                        <div>
                          <p className="font-medium text-slate-800">{registration?.booking_date}</p>
                          <p className="text-slate-600">{registration?.booking_time} (90-minute slot)</p>
                          <p className="text-slate-600">Test Centre: {registration?.test_centre || 'Langley'}, BC</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Progress Steps */}
                <div className="border-t pt-6">
                  <h3 className="font-semibold text-slate-800 mb-4">Status</h3>
                  <div className="space-y-4">
                    {/* Registration */}
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                        <CheckCircle2 className="w-5 h-5 text-green-500" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-slate-800">Registration Complete</p>
                        <p className="text-sm text-slate-500">Your information has been saved</p>
                      </div>
                      <span className="status-badge completed">Done</span>
                    </div>

                    {/* Booking */}
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                        isBookingApproved ? 'bg-green-100' : 'bg-yellow-100'
                      }`}>
                        <Calendar className={`w-5 h-5 ${
                          isBookingApproved ? 'text-green-500' : 'text-yellow-600'
                        }`} />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-slate-800">Test Appointment</p>
                        <p className="text-sm text-slate-500">
                          {isBookingApproved 
                            ? `Confirmed for ${registration?.booking_date} at ${registration?.booking_time}`
                            : 'Awaiting admin approval (within 12 hours)'}
                        </p>
                      </div>
                      <span className={`status-badge ${isBookingApproved ? 'approved' : 'pending'}`}>
                        {isBookingApproved ? 'Confirmed' : 'Pending'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Email Notice */}
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 flex items-start gap-3">
                  <Mail className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-slate-700">
                      A confirmation email will be sent to <strong>{registration?.email}</strong>
                    </p>
                    {isBookingPending && (
                      <p className="text-sm text-slate-600 mt-1">
                        You will receive another email once your booking is approved.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up animation-delay-200">
            <Button 
              variant="outline"
              className="flex-1 h-12"
              onClick={() => navigate('/')}
              data-testid="back-home-btn"
            >
              Back to Home
            </Button>
          </div>

          {/* Important Info */}
          {isBookingPending && (
            <div className="mt-6 bg-orange-50 border border-orange-200 rounded-lg p-4 animate-fade-in-up animation-delay-300">
              <div className="flex items-center gap-2 text-[#F97316] font-semibold mb-2">
                <Clock className="w-5 h-5" />
                Booking Under Review
              </div>
              <p className="text-sm text-slate-700">
                Your booking request for {registration?.booking_date} at {registration?.booking_time} is being reviewed.
                You will receive a confirmation email within 12 hours.
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
